// Garden Party — Plant Roster & Game Data
// Season 2 — opens when the garden is ready, not on a fixed date.
// SEASON_OPEN is computed in App.js from photo coverage (≥75% of active plants photographed).

export const SEASON_START = new Date('2026-03-20'); // used for photo-date filtering only

// ── ACTION DEFINITIONS ────────────────────────────────────────────────────
// cooldownDays: how many days must pass before this action can be done again
// seasonMax: max times per season (null = unlimited)
// requiresAlert: only available if plant has this alert
export const ACTION_DEFS = {
  water:     { label:'Water',       emoji:'💧', cooldownDays:1,  seasonMax:null,  alwaysAvailable:true },
  rain:      { label:'Rain watered',emoji:'🌧',  cooldownDays:1,  seasonMax:null,  alwaysAvailable:true },
  neem:      { label:'Neem Oil',    emoji:'🛡️', cooldownDays:10, seasonMax:null,  requiresType:['climbing-rose','rose'] },
  prune:     { label:'Prune',       emoji:'✂️', cooldownDays:30, seasonMax:3 },
  train:     { label:'Train/Tie',   emoji:'🪢', cooldownDays:7,  seasonMax:null },
  fertilize: { label:'Fertilize',   emoji:'🌿', cooldownDays:30, seasonMax:3 },
  photo:     { label:'Photograph',  emoji:'📸', cooldownDays:0,  seasonMax:null,  alwaysAvailable:true },
  visit:     { label:'Just visited',emoji:'👀', cooldownDays:1,  seasonMax:null,  alwaysAvailable:true },
  plant:     { label:'Plant here',  emoji:'🌱', cooldownDays:0,  seasonMax:1 },
  worms:     { label:'Add worms',   emoji:'🪱', cooldownDays:60, seasonMax:2 },
  note:      { label:'Observation', emoji:'📝', cooldownDays:0,  seasonMax:null,  alwaysAvailable:true },
  tend:      { label:'Tend',        emoji:'✨', cooldownDays:0,  seasonMax:null,  alwaysAvailable:true },
  shelter:   { label:'Cover & Shelter', emoji:'🧊', cooldownDays:0.5, seasonMax:null },
};

// ── HOW-TO INSTRUCTIONS (shown before doing the action) ───────────────────
export const ACTION_HOWTO = {
  prune: {
    'hydrangea': `Limelight hydrangeas bloom on new wood — prune in early spring before new growth starts.\n\n1. Cut all stems back to about 12–18 inches from the ground.\n2. Remove any dead or crossing branches.\n3. Leave the structure open so air can circulate.\n4. Don't worry about being too aggressive — they come back strong.`,
    'climbing-rose': `For Zéphirine Drouhin in early spring:\n\n1. Remove any dead, damaged, or crossing canes.\n2. Shorten side shoots (laterals) to 2–3 buds.\n3. Don't cut main canes unless damaged — she needs them to climb.\n4. No thorns, so you can work barehanded.`,
    'default': `Remove dead, damaged, or crossing growth. Cut just above an outward-facing bud at a 45° angle. When in doubt, less is more.`,
  },
  neem: {
    'default': `Neem oil prevents fungal disease and deters aphids on roses.\n\n1. Mix 2 tsp neem oil + 1 tsp dish soap per quart of water.\n2. Spray all leaf surfaces — top and bottom.\n3. Apply in early morning or evening, never in direct sun.\n4. Do not apply when temperatures exceed 90°F — causes leaf burn.\n5. Repeat every 10–14 days through the growing season. Taper off in July–August heat.`,
  },
  train: {
    'wisteria': `Wisteria needs regular training to follow the wire grid and not just bulk up.\n\n1. Identify new green shoots from this season.\n2. Gently wind them around the wire in the direction you want.\n3. Tie loosely with soft garden twine — never wire.\n4. Remove any shoots growing inward toward the wall.`,
    'climbing-rose': `Training keeps the Zéphirine climbing the lattice rather than arching away.\n\n1. Find new canes growing away from the lattice.\n2. Bend them gently toward the structure — she's flexible.\n3. Tie with soft twine at 2–3 points.\n4. Fan the canes outward for more blooms.`,
    'default': `Gently guide new growth in the direction you want. Tie loosely with soft twine. Don't force anything that resists.`,
  },
  fertilize: {
    'default': `Use a slow-release balanced fertilizer (10-10-10) in early spring, then a bloom booster (low nitrogen) before flowering.\n\n1. Water the plant first so roots aren't dry.\n2. Scatter granules at the drip line, not against the stem.\n3. Water in gently after applying.\n4. Don't over-fertilize — less is more.`,
  },
  worms: {
    'default': `Adding worms improves soil aeration and nutrient cycling.\n\n1. Get red wigglers or nightcrawlers from a garden center.\n2. Add a layer of compost first if you have it.\n3. Place worms on the soil surface in early morning.\n4. They'll work their way down on their own. Don't bury them.`,
  },
  shelter: {
    'default': `Cover frost-sensitive plants before temperatures drop below 32°F.\n\n1. Use burlap, frost cloth, or an old bedsheet — not plastic.\n2. Cover in the evening before the freeze, uncover the next morning once temps rise.\n3. For roses in pots, move them closer to the building wall for wind protection.\n4. Log this so you have a record of frost events for future seasons.`,
  },
};

// ── TERRACE PLANTS ────────────────────────────────────────────────────────
// pos: {x, y} fraction of terrace map (0=left/top, 1=right/bottom)
// x=0 = Wall 2 (fence), x=1 = Wall 4 (railing)
// y=0 = Wall 3 (back), y=1 = Wall 1 (building)
// moveable: true = player can drag to reposition

export const TERRACE_PLANTS = [
  {
    id:'wisteria-l', name:'Wisteria', subtitle:'The fence, north end',
    species:'Wisteria sinensis',
    type:'wisteria', wall:2,
    pos:{x:0.058, y:0.09},
    health:'thriving', color:'#9860c8',
    moveable:false,
    growth:0.18,  // 18% — woody canes on wire, new leafing out just starting
    container:'Wooden barrel planter',
    poem:'I have claimed\nthe fence, the wires, the light.\nPrune me. I dare you.',
    lore:'Established and woody, already threading the diamond wire grid. Flanks the left side of the couch. Has been here through one winter and remembers it.',
    actions:['water','train','photo','visit'],
    careWindows:{ train:{cooldownDays:7}, water:{cooldownDays:1} },
    special:null,
  },
  {
    id:'wisteria-r', name:'Wisteria', subtitle:'The fence, grill end',
    species:'Wisteria sinensis',
    type:'wisteria', wall:2,
    pos:{x:0.058, y:0.72},
    health:'thriving', color:'#9860c8',
    moveable:false,
    growth:0.22,  // 22% — has reached the third wire, slightly more ambitious
    container:'Wooden barrel planter',
    poem:'My sister reaches left.\nI reach right.\nBetween us: the couch, the fire, the years.',
    lore:'Right-side wisteria. Has already reached the third wire. Slightly more ambitious than her sister.',
    actions:['water','train','photo','visit'],
    special:null,
  },
  {
    id:'ev-c23', name:'Evergreen', subtitle:'Back-left corner',
    species:'Thuja occidentalis',
    type:'evergreen', wall:'c23',
    pos:{x:0.09, y:0.07},
    health:'thriving', color:'#4a7828',
    moveable:false,
    container:'White ribbed cylinder',
    poem:'Green through every month.\nNeither spring nor winter changes\nwhat it already knows.',
    lore:'The quiet sentinel of the northwest corner. Never complained.',
    actions:['water','visit'],
  },
  {
    id:'zephy-l', name:'Zéphirine Drouhin', subtitle:'Left cedar planter',
    species:"Rosa 'Zéphirine Drouhin'",
    type:'climbing-rose', wall:3,
    pos:{x:0.28, y:0.062},
    health:'content', color:'#e84070',
    moveable:false,
    growth:0.10,  // 10% — early spring, canes pruned, just waking up
    container:'Cedar raised planter (left)',
    poem:'Thornless, they say,\nas if softness were not\nits own kind of power.',
    lore:'Thornless, deep pink, famously fragrant. Named for a Burgundy wine merchant. Climbing the left lattice on Wall 3. The lavender at her feet keeps aphids nervous.',
    actions:['water','neem','prune','train','shelter','photo'],
    special:'zephirine',
  },
  {
    id:'lavender', name:'Lavender', subtitle:'Left planter, between the roses',
    species:'Lavandula angustifolia',
    type:'lavender', wall:3,
    pos:{x:0.42, y:0.062},
    health:'content', color:'#b890e0',
    moveable:false,
    container:'Cedar raised planter (left, center)',
    poem:'The rose gets the poems.\nI get the bees.\nWe are both content.',
    lore:'Companion planted with the Zéphirine. Silver-green, alive from last season. Confuses aphids. Asks for almost nothing.',
    actions:['water','prune','photo','visit'],
  },
  {
    id:'lavender-r', name:'Lavender', subtitle:'Right planter, between the roses',
    species:'Lavandula angustifolia',
    type:'lavender', wall:3,
    pos:{x:0.62, y:0.062},
    health:'content', color:'#b890e0',
    moveable:false,
    container:'Cedar raised planter (right, center)',
    poem:'The rose gets the poems.\nI get the bees.\nWe are both content.',
    lore:'Companion planted with the right Zéphirine. Confuses aphids. Asks for almost nothing.',
    actions:['water','prune','photo','visit'],
  },
  {
    id:'zephy-r', name:'Zéphirine Drouhin', subtitle:'Right cedar planter',
    species:"Rosa 'Zéphirine Drouhin'",
    type:'climbing-rose', wall:3,
    pos:{x:0.60, y:0.062},
    health:'content', color:'#e84070',
    moveable:false,
    growth:0.10,
    container:'Cedar raised planter (right)',
    poem:'My sister to the left.\nThe same thorns we lack.\nThe same perfume, come June.',
    lore:'Second Zéphirine Drouhin on Wall 3 — same species, same beauty. Training up the right lattice panel.',
    actions:['water','neem','prune','train','shelter','photo'],
    special:'zephirine',
  },
  {
    id:'ev-c34', name:'Evergreen', subtitle:'Back-right corner',
    species:'Thuja occidentalis',
    type:'evergreen', wall:'c34',
    pos:{x:0.915, y:0.07},
    health:'thriving', color:'#4a7828',
    moveable:false,
    container:'White ribbed cylinder',
    poem:'Across the wall, my twin.\nWe have not spoken.\nWe do not need to.',
    lore:'Mirror of the Wall 2/3 evergreen. Bookends the north wall with quiet permanence.',
    actions:['water','visit'],
  },
  {
    id:'serviceberry', name:'Serviceberry', subtitle:"Emma's wedding gift",
    species:'Amelanchier canadensis',
    type:'serviceberry', wall:4,
    pos:{x:0.930, y:0.28},
    health:'thriving', color:'#d06030',
    moveable:false,
    container:'Grey glazed cylinder',
    poem:'She planted me\nthe year they married.\nI remember everything.',
    lore:"A wedding gift for Emma. Planted the first spring here. The coral-red branches glow in winter light. Spring: white blossoms. Summer: small purple berries for birds. It takes its role seriously.",
    actions:['water','shelter','photo','visit'],
    special:'wedding',
  },
  {
    id:'hydrangea-1', name:'Limelight Hydrangea', subtitle:'First pot · north',
    species:"Hydrangea paniculata 'Limelight'",
    type:'hydrangea', wall:4,
    pos:{x:0.930, y:0.43},
    health:'content', color:'#b8c8e0',
    moveable:false,
    container:'Large grey round pot',
    poem:'My old flowers hang on\nlike stories no one\nhas asked me to stop telling.',
    lore:'First of four. Still holding last season\'s papery dried heads. Needs pruning before new growth starts.',
    actions:['prune','water','fertilize','photo'],
  },
  {
    id:'hydrangea-2', name:'Limelight Hydrangea', subtitle:'Second pot',
    species:"Hydrangea paniculata 'Limelight'",
    type:'hydrangea', wall:4,
    pos:{x:0.930, y:0.528},
    health:'content', color:'#b8c8e0',
    moveable:false,
    container:'Large grey round pot',
    poem:'Did you hear\nwhat the serviceberry said?\nNeither did I. But still.',
    lore:'Second hydrangea. Needs pruning.',
    actions:['prune','water','fertilize','photo'],
  },
  {
    id:'hydrangea-3', name:'Limelight Hydrangea', subtitle:'Third pot',
    species:"Hydrangea paniculata 'Limelight'",
    type:'hydrangea', wall:4,
    pos:{x:0.930, y:0.626},
    health:'content', color:'#b8c8e0',
    moveable:false,
    container:'Large grey round pot',
    poem:'The maple is so quiet.\nI have been trying\nto learn from this.',
    lore:'Third hydrangea. Always slightly behind the others in blooming. Makes up for it in September.',
    actions:['prune','water','fertilize','photo'],
  },
  {
    id:'hydrangea-4', name:'Limelight Hydrangea', subtitle:'Fourth pot · closest to the maple',
    species:"Hydrangea paniculata 'Limelight'",
    type:'hydrangea', wall:4,
    pos:{x:0.930, y:0.724},
    health:'content', color:'#b8c8e0',
    moveable:false,
    container:'Large grey round pot',
    poem:'Four of us now.\nThe railing has never been\nso well attended.',
    lore:'Fourth hydrangea, closest to the Japanese maple. Recently noticed.',
    actions:['prune','water','fertilize','photo'],
  },
  {
    id:'maple', name:'Japanese Maple', subtitle:"Friend's gift",
    species:'Acer palmatum',
    type:'maple', wall:4,
    pos:{x:0.930, y:0.805},
    health:'thriving', color:'#d85828',
    moveable:false,
    container:'Grey glazed pot',
    poem:'A friend carried me here.\nI have been thinking about that\never since.',
    lore:'A gift from a friend. Delicate, philosophical, fine-branching. Deep crimson in autumn. Has survived one Brooklyn winter.',
    actions:['water','photo','visit'],
    special:'gift',
  },
  {
    id:'ev-xmas', name:'Evergreen', subtitle:'The holiday returnee',
    species:'Thuja occidentalis',
    type:'evergreen', wall:'c41',
    pos:{x:0.930, y:0.920},
    health:'recovering', color:'#888040',
    moveable:false,
    container:'White ribbed cylinder',
    poem:'It remembers lights.\nNow rain and cold again—\nboth are also home.',
    lore:'Spent winter indoors as your Christmas tree. Decorated with ornaments, admired, returned to the terrace in January. Slightly browned. Adjusting to outdoor life.',
    actions:['water','fertilize','photo'],
    special:'xmas',
  },
  // Empty / moveable pots
  {
    id:'pot-midcentury', name:'Empty Pot', subtitle:'White ceramic on legs',
    type:'empty-pot', wall:2,
    pos:{x:0.08, y:0.50},
    health:'empty', color:'#d0d0c0',
    moveable:true,
    container:'White ceramic on mid-century wood legs',
    poem:'My legs are beautiful.\nFill me with something worthy.',
    lore:'The most stylish empty vessel on the terrace. A prominent position near the couch.',
    actions:['plant'],
  },
  {
    id:'pot-pink', name:'Empty Pot', subtitle:'Pink chalice',
    type:'empty-pot', wall:1,
    pos:{x:0.54, y:0.915},
    health:'empty', color:'#e8a8c8',
    moveable:true,
    container:'Pink chalice ceramic (on table)',
    poem:'Pink and waiting.\nSomething cheerful\nwould be nice.',
    lore:'Charming pink ceramic on the dining table. The last resident did not survive winter.',
    actions:['plant'],
  },
  {
    id:'pot-white-1', name:'Empty Pot', subtitle:'White ribbed round',
    type:'empty-pot', wall:1,
    pos:{x:0.22, y:0.915},
    health:'empty', color:'#d8d8d0',
    moveable:true,
    container:'White ribbed round with saucer (near grill)',
    poem:'Near the grill.\nReady for summer.',
    lore:'A solid white ribbed pot near the grill area.',
    actions:['plant'],
  },
  {
    id:'pot-blue', name:'Empty Pot', subtitle:'Blue/slate ribbed',
    type:'empty-pot', wall:1,
    pos:{x:0.34, y:0.915},
    health:'empty', color:'#a8b8c8',
    moveable:true,
    container:'Blue/slate ribbed round with saucer',
    poem:'Small but here.\nReady.',
    lore:'Smaller blue-grey pot. Good for herbs or something textural.',
    actions:['plant'],
  },
  {
    id:'sill-l', name:'Window Box', subtitle:'Left · In memoriam',
    type:'memorial', wall:1,
    pos:{x:0.62, y:0.875},
    health:'memorial', color:'#907060',
    moveable:false,
    container:'New planter (planned)',
    poem:'† Begonia, 2025\nYou were colorful.\nYou are remembered.',
    lore:'The begonias that lived here did not survive last winter. New planters are planned for this season.',
    actions:['plant'],
  },
  {
    id:'sill-r', name:'Window Box', subtitle:'Right · In memoriam',
    type:'memorial', wall:1,
    pos:{x:0.74, y:0.875},
    health:'memorial', color:'#907060',
    moveable:false,
    container:'New planter (planned)',
    poem:'† Begonia, 2025\nSpring is coming.\nYou will be replaced with love.',
    lore:'The second begonia planter. Awaiting its successor.',
    actions:['plant'],
  },
];

export const ALL_PLANTS = [...TERRACE_PLANTS];

// ── FRONT GARDEN PLANTS (Emma's Rose Garden + Magnolia) ──────────────────
export const FRONT_PLANTS = [
  { id:'magnolia', name:'Magnolia', subtitle:'Back of garden', species:"Magnolia x soulangeana",
    type:'magnolia', pos:{x:0.82, y:0.13}, health:'resting', color:'#e8a0c0', moveable:false,
    container:"in ground (Emma's Rose Garden)", gardenSection:"Emma's Rose Garden",
    poem:'Before the leaves,\nthe blossoms open like hands—\npink into the grey.',
    lore:'The queen of the garden. Blooms in early April before any leaves appear.',
    actions:['visit','photo'], noTasks: true },
  { id:'dko-1', name:"Double Knock Out Rose", subtitle:'Left column, top', species:"Rosa 'Double Knock Out'",
    type:'rose', pos:{x:0.19, y:0.18}, health:'resting', color:'#e84070', moveable:false,
    container:"in ground (Emma's Rose Garden)", gardenSection:"Emma's Rose Garden",
    poem:'Six in a row, all\npruned back to sticks. They know what\ncomes after the cold.',
    lore:"Emma's Rose Garden. Bulletproof.",
    actions:['water','neem','prune','fertilize','shelter','photo'] },
  { id:'dko-2', name:"Double Knock Out Rose", subtitle:'Left column, middle', species:"Rosa 'Double Knock Out'",
    type:'rose', pos:{x:0.19, y:0.52}, health:'resting', color:'#e84070', moveable:false,
    container:"in ground (Emma's Rose Garden)", gardenSection:"Emma's Rose Garden",
    poem:'Center front. Best spot.\nI did not ask for this.\nI am grateful anyway.',
    lore:'Center position, most sun.',
    actions:['water','neem','prune','fertilize','shelter','photo'] },
  { id:'dko-3', name:"Double Knock Out Rose", subtitle:'Left column, bottom', species:"Rosa 'Double Knock Out'",
    type:'rose', pos:{x:0.19, y:0.84}, health:'resting', color:'#e84070', moveable:false,
    container:"in ground (Emma's Rose Garden)", gardenSection:"Emma's Rose Garden",
    poem:'Still as bare wire.\nStill as everything waiting—\nstill as what will bloom.',
    lore:'The third front rose.',
    actions:['water','neem','prune','fertilize','shelter','photo'] },
  { id:'dko-4', name:"Double Knock Out Rose", subtitle:'Center column, top', species:"Rosa 'Double Knock Out'",
    type:'rose', pos:{x:0.50, y:0.30}, health:'resting', color:'#e84070', moveable:false,
    container:"in ground (Emma's Rose Garden)", gardenSection:"Emma's Rose Garden",
    poem:null, lore:'Middle row left.',
    actions:['water','neem','prune','fertilize','shelter','photo'] },
  { id:'dko-5', name:"Double Knock Out Rose", subtitle:'Center column, bottom', species:"Rosa 'Double Knock Out'",
    type:'rose', pos:{x:0.50, y:0.62}, health:'resting', color:'#e84070', moveable:false,
    container:"in ground (Emma's Rose Garden)", gardenSection:"Emma's Rose Garden",
    poem:null, lore:'Middle row right.',
    actions:['water','neem','prune','fertilize','shelter','photo'] },
  { id:'dko-6', name:"Double Knock Out Rose", subtitle:'Bottom right', species:"Rosa 'Double Knock Out'",
    type:'rose', pos:{x:0.80, y:0.84}, health:'resting', color:'#e84070', moveable:false,
    container:"in ground (Emma's Rose Garden)", gardenSection:"Emma's Rose Garden",
    poem:"Six red roses wait\nbeneath the brownstone's shadow—\nEmma's garden sleeps.",
    lore:'Near the stoop. Every visitor sees this one.',
    actions:['water','neem','prune','fertilize','shelter','photo'] },
];
